import React, { useState } from 'react';
import { DateFormat } from '../../utils/chartTypes';
import Icon from 'components/commons/icon/Icon';
import MessageModal from 'components/features/modal/MessageModal';
import { ReportTransition } from 'config/api';

type Tagged<S extends symbol, T> = { [key in S]: never } & T;
const TOTAL = Symbol('TOTAL');

const PREV_TOTAL = Symbol('PREV_TOTAL');
const UNFLOORED = Symbol('UNFLOORED');
export type TotalAssetAmount = Tagged<typeof TOTAL, number>;
export const createTotalAssetAmount = (v: number): TotalAssetAmount =>
  v as TotalAssetAmount;
export type PrevTotalAssetAmount = Tagged<typeof PREV_TOTAL, number>;
export const createPrevTotalAssetAmount = (v: number): PrevTotalAssetAmount =>
  v as PrevTotalAssetAmount;

type ReportAssetTypes = Exclude<
  keyof ReportTransition,
  'Day' | 'TotalAsset' | 'Incorrect'
>;
type RawChartDataWithoutAssetTypes = {
  date: Date;
  prevTotalAssetAmount: PrevTotalAssetAmount;
};
type IncorrectType = {
  Incorrect: boolean;
};
export type RawChartData = Tagged<
  typeof UNFLOORED,
  (RawChartDataWithoutAssetTypes &
    IncorrectType &
    {
      [key in ReportAssetTypes]: number | null;
    })[]
>;
export const createRawChartData = (
  v: ({ date: Date; prevTotalAssetAmount: PrevTotalAssetAmount } & {
    [key in ReportAssetTypes]: number | null;
  })[]
): RawChartData => (v as unknown) as RawChartData;
export type ChartProps = {
  chartData: RawChartData;
  prevTotalAssetAmount: PrevTotalAssetAmount;
  xAxisInfo: Highcharts.XAxisOptions;
  periodVerbose: string;
  dateFormat: DateFormat;
};
type DataEmptyProps = {
  periodUnit: string;
};
export type AssetTransitionChartProps = {
  Chart: React.FC<ChartProps>;
  data: ChartProps;
  periodUnit: string;
};

const DataEmpty: React.FC<DataEmptyProps> = ({ periodUnit }) => {
  return (
    <>
      <img
        className="no-data-img"
        src="/static/images/report/chart_sample.png"
        alt=""
      />
      <span className="no-data-info">
        資産連携すると1{periodUnit}の資産のうごきがチャートで表示されます
      </span>
    </>
  );
};

export const AssetTransitionChart: React.FC<AssetTransitionChartProps> = ({
  periodUnit,
  data,
  Chart,
}) => {
  const isEmptyData = !data.chartData.length;
  const title = `1${periodUnit}の資産推移`;
  const [showModal, setShowModal] = useState(false);
  const modalShow = (): void => {
    setShowModal(true);
  };
  const modalClose = (): void => {
    setShowModal(false);
  };

  return (
    <>
      <div>
        <div className="flex">
          <div className="flex items-center text-base font-bold text-bluegray-700">
            {title}
          </div>
          <div
            className="text-gray-500 text-sm ml-auto cursor-pointer"
            onClick={modalShow}>
            <span>
              <Icon className="pr-1" name="info" />
              資産データについて
            </span>
          </div>
        </div>
      </div>
      <div className={`asset-info-bar-graph ${isEmptyData ? 'no-data' : ''}`}>
        {isEmptyData ? (
          <DataEmpty periodUnit={periodUnit} />
        ) : (
          <Chart {...data} />
        )}
      </div>
      {showModal && (
        <div onClick={modalClose}>
          <MessageModal
            title={''}
            message={[
              '「みんかぶ口座連携」で連携した口座は土日祝日はデータ更新を行いません。',
              '前回取得成功したデータを暫定値として表示しています。',
            ]}
            onClose={modalClose}
          />
        </div>
      )}
    </>
  );
};

// util

export const getTotalAssetAmount = (data: RawChartData[number]): number => {
  const nums = Object.entries(data)
    .filter(e => {
      const [k] = e;
      const obj: { [key in keyof RawChartDataWithoutAssetTypes]: 0 } = {
        date: 0,
        prevTotalAssetAmount: 0,
      };
      return !Object.keys(obj).includes(k);
    })
    .map(v => v[1]) as number[];
  return nums.reduce((acc, v) => {
    return acc + v;
  });
};
